/**
 * shadcn Badge — status indicators, labels, and count badges.
 *
 * Source: `npx shadcn@latest add badge` (base-nova style, Tailwind v4).
 * Extended with `size` axis for count badge use cases
 * (navbar unread counts, chat channel preview badges).
 *
 * Variants: default | secondary | muted | destructive | outline | ghost | warning
 *           | food | symptom | stool | activity | water | weight (journal entry types)
 * Sizes:    default (pill label) | sm (count badge)
 *
 * @see docs/styling.md
 */
import { cn } from '@web-app/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default:
          'border border-transparent bg-primary-legacy text-primary-foreground',
        secondary:
          'border border-transparent bg-secondary text-secondary-foreground',
        muted: 'border border-transparent bg-muted text-muted-foreground',
        destructive:
          'border border-transparent bg-destructive text-destructive-foreground focus-visible:ring-destructive/20',
        outline: 'border border-border-legacy text-foreground-legacy',
        ghost: '',
        warning: 'border border-transparent bg-orange-200 text-orange-500',
        food: 'bg-badge-food text-badge-food-foreground',
        symptom: 'bg-badge-symptom text-badge-symptom-foreground',
        stool: 'bg-badge-stool text-badge-stool-foreground',
        activity: 'bg-badge-activity text-badge-activity-foreground',
        water: 'bg-badge-water text-badge-water-foreground',
        weight: 'bg-badge-weight text-badge-weight-foreground',
      },
      size: {
        default: 'rounded-full px-2 py-0.5 text-xs',
        sm: 'rounded-[14px] min-h-[18px] min-w-[18px] px-[5px] pt-[2px] pb-px text-[11px] font-bold leading-normal',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  size = 'default',
  style,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      style={style}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
